import React from "react";

import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Button, Divider, Section } from "@samskip/frontend-components";
import {
  Control,
  useFieldArray,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ShippingTypeBookingTemplates } from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import { defaultDetailForm } from "@assets/formTypes/bookingTemplates";
import { useAppSelector } from "@assets/hooks";
import { CargoPackageParentType, Commodity, SelectOption } from "@assets/types";
import { isPackageRefrigerated } from "@assets/utilities/packageUtilities";
import {
  containerTypesState,
  packageTypesState,
} from "@state/slices/registrySlice";

import CargoDetailsSection from "./cargoDetailsSection";

type CargoSectionProps = {
  control: Control<BookingTemplateFormTypes.CargoForm>;
  onCommodityClear: (cargoIndex: number) => void;
  onShipmentPackagingTypeClear: (cargoIndex: number) => void;
  onTemperatureClear: (cargoIndex: number) => void;
  onTemperatureContainerChosen: (cargoIndex: number) => void;
  register: UseFormRegister<BookingTemplateFormTypes.CargoForm>;
  onTemperatureControlChange: (cargoIndex: number) => void;
  onTemperatureOutOfBounds: (cargoIndex: number) => void;
};

const CargoSection = ({
  control,
  onCommodityClear,
  onShipmentPackagingTypeClear,
  onTemperatureClear,
  onTemperatureContainerChosen,
  onTemperatureControlChange,
  onTemperatureOutOfBounds,
  register,
}: CargoSectionProps) => {
  const { t } = useTranslation();

  const containerTypes = useAppSelector(containerTypesState);
  const packageTypes = useAppSelector(packageTypesState);

  const shippingType = useWatch({ control, name: "ShippingType" });
  const {
    fields: details,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "Details",
  });

  const onRemove = (index: number) => {
    remove(index);
  };

  const handleCommodityChange = (
    index: number,
    commodity: Commodity | null
  ) => {
    if (!commodity) {
      onCommodityClear(index);
    }
  };

  const handleTemperatureChange = (
    index: number,
    newValue: SelectOption<number> | null
  ) => {
    if (!newValue) {
      onTemperatureClear(index);
    }
  };

  const handleShipmentPackagingTypeChange = (
    index: number,
    newPackage: CargoPackageParentType | null
  ) => {
    if (!newPackage) {
      onShipmentPackagingTypeClear(index);
    }
    if (isPackageRefrigerated(newPackage)) {
      onTemperatureContainerChosen(index);
    }
  };

  const handleTemperatureControlChange = (index: number) => {
    onTemperatureControlChange(index);
  };

  return (
    <Section
      title={
        shippingType === ShippingTypeBookingTemplates.LL
          ? t("LABEL_CARGO")
          : t("LABEL_CONTAINER")
      }
    >
      {details.map((detail, index) => (
        <div className="flex flex-col gap-8" key={detail.id}>
          {index > 0 && <Divider />}
          <CargoDetailsSection
            control={control}
            index={index}
            isRemovable={details.length > 1}
            onCommodityChange={(commodity) =>
              handleCommodityChange(index, commodity)
            }
            onPackageTypeChange={(packageType) =>
              handleShipmentPackagingTypeChange(index, packageType)
            }
            onRemove={() => onRemove(index)}
            onTemperatureChange={(newTemp) =>
              handleTemperatureChange(index, newTemp)
            }
            onTemperatureControlChange={() =>
              handleTemperatureControlChange(index)
            }
            onTemperatureOutOfBounds={() => onTemperatureOutOfBounds(index)}
            register={register}
            shipmentPackageTypes={
              shippingType === ShippingTypeBookingTemplates.LL
                ? packageTypes
                : containerTypes
            }
            shippingType={shippingType}
          />
        </div>
      ))}
      <Button
        iconLeft={faPlus}
        onClick={() => {
          append(defaultDetailForm);
        }}
        variant="text"
      >
        {t("LABEL_ADD_LINE")}
      </Button>
    </Section>
  );
};

export default CargoSection;
